import React, { useEffect, useState } from "react";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import { IKeycloak, useKeycloak } from "./Keycloak/useKeycloak";
import { KeycloakProvider } from "./Keycloak/keycloakContext";
import { useUserStore } from "../stores/userStore";
import AuthLoading from "../pages/AuthLoading";
import { keycloakClientId, keycloakRealm, keycloakUrl } from "../config/config";

const MAX_RETRIES = 3;
const RETRY_COUNT_KEY = "loginRetryCount";

export const keycloakConfig: IKeycloak = {
    dev: {
        clientId: keycloakClientId,
        realm: keycloakRealm,
        url: keycloakUrl,
    },
    staging: {
        clientId: keycloakClientId,
        realm: keycloakRealm,
        url: keycloakUrl,
    },
    prod: {
        clientId: keycloakClientId,
        realm: keycloakRealm,
        url: keycloakUrl,
    },
};

const keycloakOptions: KeycloakInitOptions = {
    flow: "standard",
    onLoad: "login-required",
    checkLoginIframe: false,
};

interface KeycloakWrapperProps {
    children: React.ReactNode;
}

const KeycloakWrapper: React.FC<KeycloakWrapperProps> = ({ children }) => {
    const { keycloak, isAuthenticated, isAuthenticating } = useKeycloak(
        keycloakConfig,
        keycloakOptions,
        "prod"
    );

    // swap `any` for your real state type if you have one
    const setUser = useUserStore((state: any) => state.setUser);
    const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);

    // once authenticated, pull user info into Zustand
    useEffect(() => {
        if (isAuthenticated && keycloak?.tokenParsed) {
            const tokenParsed = keycloak.tokenParsed;
            setUser({
                username: tokenParsed.preferred_username,
                roles: tokenParsed.realm_access?.roles,
                givenName: tokenParsed.given_name,
                familyName: tokenParsed.family_name,
                sub: tokenParsed.sub,
                token: tokenParsed,
            });
        }
    }, [isAuthenticated, keycloak, setUser]);

    // retry login on adapter errors
    useEffect(() => {
        if ((keycloak as any)?.error) {
            const stored = sessionStorage.getItem(RETRY_COUNT_KEY);
            const retryCount = stored ? parseInt(stored, 10) : 0;

            if (retryCount < MAX_RETRIES) {
                sessionStorage.setItem(RETRY_COUNT_KEY, (retryCount + 1).toString());
                console.warn(`Login error, retry ${retryCount + 1}`);
                // ← use optional chaining on keycloak itself
                keycloak?.login?.();
            } else {
                sessionStorage.setItem(RETRY_COUNT_KEY, "0");
                setLoginErrorMessage(
                    "Die Authentifizierung ist nach mehreren Versuchen fehlgeschlagen."
                );
            }
        }
    }, [(keycloak as any)?.error, keycloak]);

    if (isAuthenticating) {
        return <AuthLoading />;
    }
    if (!isAuthenticated) {
        return loginErrorMessage ? <div>{loginErrorMessage}</div> : null;
    }

    return (
        <KeycloakProvider value={{ keycloak, isAuthenticated, isAuthenticating }}>
            {children}
        </KeycloakProvider>
    );
};

export default KeycloakWrapper;
